/**
 * useMentionAutocomplete — wires an input/textarea's value + caret to the
 * mention provider registry, returning debounced match results for the
 * currently active `@query` (if any).
 */

import { useEffect, useRef, useState } from 'react';
import {
  getAllMentionProviders,
  hasAnyProvider,
  MentionMatch,
  MentionProvider,
} from '../registry';
import { ActiveQuery, detectActiveQuery } from '../parse';

export interface MentionAutocompleteResult {
  active: ActiveQuery | null;
  matches: Array<{ provider: MentionProvider; match: MentionMatch }>;
  loading: boolean;
}

const DEBOUNCE_MS = 150;

export function useMentionAutocomplete(
  value: string,
  caret: number,
): MentionAutocompleteResult {
  const [matches, setMatches] = useState<
    Array<{ provider: MentionProvider; match: MentionMatch }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<ActiveQuery | null>(null);
  const reqSeqRef = useRef(0);

  useEffect(() => {
    if (!hasAnyProvider()) {
      if (active !== null) setActive(null);
      if (matches.length) setMatches([]);
      return;
    }
    const q = detectActiveQuery(value, caret);
    setActive(q);
    if (!q) {
      setMatches([]);
      setLoading(false);
      return;
    }
    // Debounce
    setLoading(true);
    const seq = ++reqSeqRef.current;
    const handle = window.setTimeout(async () => {
      const providers = getAllMentionProviders();
      const results: Array<{ provider: MentionProvider; match: MentionMatch }> = [];
      try {
        const all = await Promise.all(
          providers.map(async (p) => {
            try {
              const rs = await p.search(q.query);
              return { p, rs };
            } catch {
              return { p, rs: [] as MentionMatch[] };
            }
          }),
        );
        // Ignore stale responses
        if (seq !== reqSeqRef.current) return;
        for (const { p, rs } of all) {
          for (const match of rs) {
            results.push({ provider: p, match });
          }
        }
        setMatches(results);
      } finally {
        if (seq === reqSeqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
    // Intentionally depends only on value + caret — we derive state from them.
  }, [value, caret]);

  return { active, matches, loading };
}
